import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  pending: 'En attente',
  marketing_validated: 'Validé Marketing',
  force_de_vente_validated: 'Validé Animateur',
  directrice_validated: 'Validé Directrice',
  completed: 'Complété',
  commercial_rejected: 'Refusé Commercial',
  marketing_rejected: 'Refusé Marketing',
  force_de_vente_rejected: 'Refusé Animateur',
};

const SupplyChainReport = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '', region: '' });
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    api.get('/api/distributors/regions')
      .then(res => setRegions(res.data.data || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/api/reports/supply-chain?${params}`);
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/api/reports/supply-chain/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport-supply-chain-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Erreur d\'export');
    }
  };

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };

  const LIMIT = 20;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Rapport Supply Chain</h1>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export Excel
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Du</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Au</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Statut</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="completed">Complété</option>
              <option value="pending">En attente</option>
              <option value="marketing_validated">Validé Marketing</option>
              <option value="force_de_vente_validated">Validé FDV</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Région</label>
            <select
              name="region"
              value={filters.region}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Toutes les régions</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-800 text-white">
            <tr>
              {['N° de note', 'Date', 'Statut', 'Client', 'Code SAP', 'Désignation article', 'Quantité', 'Distributeur', 'Région', 'Animateur'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={10} className="text-center py-8 text-gray-400">Chargement...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-8 text-gray-400">Aucune donnée</td></tr>
            ) : data.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-xs">{row.referenceId || row.requestId?.toString().substring(0, 8)}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.createdAt ? new Date(row.createdAt).toLocaleDateString('fr-FR') : ''}</td>
                <td className="px-4 py-2 whitespace-nowrap">{STATUS_LABELS[row.status] || row.status}</td>
                <td className="px-4 py-2">{row.externalUserName}</td>
                <td className="px-4 py-2 font-mono">{row.sapCode || '—'}</td>
                <td className="px-4 py-2">{row.productName}</td>
                <td className="px-4 py-2 text-center">{row.quantity}</td>
                <td className="px-4 py-2">{row.distributorName || '—'}</td>
                <td className="px-4 py-2">{row.region || '—'}</td>
                <td className="px-4 py-2">{row.animatorName || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between items-center px-4 py-3 border-t text-sm">
          <span className="text-gray-500">Total: {total} entrées</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Précédent
            </button>
            <span className="px-3 py-1">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * LIMIT >= total}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplyChainReport;
